import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import Layout from '@/components/Layout/Layout';
import DetailCard, { ICardDetail } from '@/components/common/DetailCard';
import FullPageLoader from '@/components/loader/FullPageLoader';
import BackButton from '@/components/ui/BackButton';

import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification, capitalizeFirstLetter } from '@/utility/helper';

interface IEmployeeUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  employeeCode: string;
  profilePhoto?: string;
  department?: { name: string };
  designation?: { name: string };
}

interface IOffboardingDetail {
  id: string;
  OffBoardType: string;
  reason: string;
  joiningDate: string;
  lastWorkingDay: string;
  tenure: number;
  assetHandover: boolean;
  isCompletedProject: boolean;
  status: string;
  userId: string;
  handoverId: string;
  employeeUser: IEmployeeUser;
  handoverUser: IEmployeeUser;
  createdAt: string;
  updatedAt: string;
}

interface IOffboardingDetailResponse {
  status: boolean;
  message?: string;
  data: IOffboardingDetail;
}

const OffboardingDetail: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [detail, setDetail] = useState<IOffboardingDetail | null>(null);
  const { id } = useParams<{ id: string }>();

  const getStatusBadge = (status: string) => {
    const map: Record<string, { color: string; bg: string }> = {
      Initiated: { color: '#0056b3', bg: '#cce5ff' },
      'In Progress': { color: '#856404', bg: '#fff3cd' },
      Completed: { color: '#155724', bg: '#d4edda' },
    };
    const style = map[status] || { color: '#6c757d', bg: '#e9ecef' };
    return (
      <span
        style={{
          padding: '4px 12px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: 500,
          color: style.color,
          backgroundColor: style.bg,
          display: 'inline-block',
        }}
      >
        {status}
      </span>
    );
  };

  const getOffboardingDetail = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const response: IOffboardingDetailResponse = await callApi(
        API_URL + API_ENDPOINT.offbordingDetails + `/${id}`,
        { method: 'GET' },
      );

      if (response?.status) {
        setDetail(response.data);
      } else {
        showToastNotification(
          'danger',
          response?.message || 'Failed to fetch details',
        );
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getOffboardingDetail();
  }, [id]);

  const offboardingDetails: ICardDetail[] = [
    { label: 'Offboard Type', value: detail?.OffBoardType ?? '-' },
    { label: 'Reason', value: detail?.reason ?? '-' },
    {
      label: 'Joining Date',
      value: detail?.joiningDate
        ? moment(detail.joiningDate).format('DD-MMM-YYYY')
        : '--',
    },
    {
      label: 'Last Working Day',
      value: detail?.lastWorkingDay
        ? moment(detail.lastWorkingDay).format('DD-MMM-YYYY')
        : '--',
    },
    { label: 'Tenure (yrs)', value: detail?.tenure ?? '--' },
    { label: 'Asset Handover', value: detail?.assetHandover ? 'Yes' : 'No' },
    {
      label: 'Project Completed',
      value: detail?.isCompletedProject ? 'Yes' : 'No',
    },
    {
      label: 'Status',
      value: detail?.status ? getStatusBadge(detail.status) : '--',
    },
    {
      label: 'Created At',
      value: detail?.createdAt
        ? moment(detail.createdAt).format('DD-MMM-YYYY hh:mm A')
        : '--',
    },
    {
      label: 'Updated At',
      value: detail?.updatedAt
        ? moment(detail.updatedAt).format('DD-MMM-YYYY hh:mm A')
        : '--',
    },
  ];

  const employeeDetails: ICardDetail[] = [
    {
      label: 'Name',
      value: capitalizeFirstLetter(detail?.employeeUser?.name ?? '-'),
    },
    {
      label: 'Employee Code',
      value: detail?.employeeUser?.employeeCode ?? '-',
    },
    { label: 'Email', value: detail?.employeeUser?.email ?? '-' },
    { label: 'Phone', value: detail?.employeeUser?.phone ?? '-' },
    {
      label: 'Department',
      value: detail?.employeeUser?.department?.name ?? '-',
    },
    {
      label: 'Designation',
      value: detail?.employeeUser?.designation?.name ?? '-',
    },
  ];

  const handoverDetails: ICardDetail[] = [
    {
      label: 'Name',
      value: capitalizeFirstLetter(detail?.handoverUser?.name ?? '-'),
    },
    {
      label: 'Employee Code',
      value: detail?.handoverUser?.employeeCode ?? '-',
    },
    { label: 'Email', value: detail?.handoverUser?.email ?? '-' },
    { label: 'Phone', value: detail?.handoverUser?.phone ?? '-' },
    {
      label: 'Department',
      value: detail?.handoverUser?.department?.name ?? '-',
    },
    {
      label: 'Designation',
      value: detail?.handoverUser?.designation?.name ?? '-',
    },
  ];

  return (
    <Layout>
      {isLoading ? (
        <FullPageLoader />
      ) : (
        <div className="content container-fluid">
          <BackButton isDetail={true} label="Offboarding Detail" />

          <div className="row">
            <div className="col-12 col-lg-12">
              <DetailCard
                title="Offboarding Details"
                cardDetails={offboardingDetails}
                profile={
                  detail?.employeeUser?.profilePhoto ||
                  '/assets/img/employee.svg'
                }
              />
            </div>
            <div className="col-12 col-lg-6">
              <DetailCard
                title="Employee Information"
                cardDetails={employeeDetails}
              />
            </div>
            <div className="col-12 col-lg-6">
              <DetailCard
                title="Handover Person"
                cardDetails={handoverDetails}
              />
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default OffboardingDetail;
